import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { leadFormEventName } from "@/lib/lead-form";
import { leadFormCalculatorEventName } from "@/components/Calculator";
import { useCallback, useEffect, useRef, useState } from "react";
import type intlTelInput from "intl-tel-input";
import { toast } from "@/hooks/use-toast";

type IntlTelInputFactory = typeof intlTelInput;
type IntlTelInstance = ReturnType<IntlTelInputFactory>;

declare global {
  interface Window {
    intlTelInputUtils?: {
      numberFormat: {
        E164: number;
        INTERNATIONAL: number;
        NATIONAL: number;
        RFC3966: number;
      };
    };
    dataLayer?: unknown[];
  }
}

type CalculatorData = {
  tipoContrato: string;
  taxaCobrada: number;
  percentualAcima: number;
  valorPagoAMais: number;
};

const formatBrazilianNationalNumber = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;

  const ddd = digits.slice(0, 2);
  const number = digits.slice(2);

  if (number.length <= 4) return `(${ddd}) ${number}`;
  if (number.length <= 8) return `(${ddd}) ${number.slice(0, 4)}-${number.slice(4)}`;
  return `(${ddd}) ${number.slice(0, 5)}-${number.slice(5)}`;
};

const isValidBrazilianNumber = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 11) return false;
  const ddd = Number(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  return true;
};

const sanitizeText = (value: string) => value.replace(/[<>]/g, "").trim();

const getCookieValue = (name: string) => {
  if (typeof document === "undefined") return undefined;
  const cookies = document.cookie?.split(";") ?? [];
  const target = cookies
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));
  if (!target) return undefined;
  const [, value] = target.split("=", 2);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const MIN_FORM_OPEN_TIME_MS = 2500;
const RESUBMIT_COOLDOWN_MS = 15000;

const LeadFormDialog = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    telefone: "",
  });
  const [phoneError, setPhoneError] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [hideFloatingCard, setHideFloatingCard] = useState(false);
  const [honeypotValue, setHoneypotValue] = useState("");
  const [formOpenTimestamp, setFormOpenTimestamp] = useState<number | null>(null);
  const [calculatorData, setCalculatorData] = useState<CalculatorData | null>(null);
  const lastSubmissionTimestampRef = useRef<number | null>(null);
  const submissionLockRef = useRef(false);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const [isPhoneInputReady, setIsPhoneInputReady] = useState(false);
  const [isIntlTelInputReady, setIsIntlTelInputReady] = useState(false);
  const handlePhoneInputRef = useCallback((node: HTMLInputElement | null) => {
    phoneInputRef.current = node;
    setIsPhoneInputReady(Boolean(node));
  }, []);
  const intlTelInputFactoryRef = useRef<IntlTelInputFactory | null>(null);
  const intlTelInputUtilsUrlRef = useRef<string | null>(null);
  const itiRef = useRef<IntlTelInstance | null>(null);

  const getDialCodeFromDom = useCallback(() => {
    const dialCodeElement = phoneInputRef.current
      ?.closest(".iti")
      ?.querySelector<HTMLElement>(".iti__selected-dial-code");
    const dialCodeText = dialCodeElement?.textContent ?? "";
    return dialCodeText.replace(/[^\d]/g, "");
  }, []);

  const syncPhoneFromInput = useCallback(
    (markTouched = false) => {
      const inputEl = phoneInputRef.current;
      const itiInstance = itiRef.current;
      if (!inputEl || !itiInstance) return;

      if (markTouched) setPhoneTouched(true);

      const selectedCountry = itiInstance.getSelectedCountryData();
      const domDialCode = getDialCodeFromDom();
      const isBrazil =
        domDialCode === "55" ||
        selectedCountry?.iso2 === "br" ||
        selectedCountry?.dialCode === "55";

      if (isBrazil) {
        inputEl.value = formatBrazilianNationalNumber(inputEl.value);
      }

      const utils = typeof window !== "undefined" ? window.intlTelInputUtils : undefined;
      let storedValue = inputEl.value;

      if (utils) {
        storedValue =
          itiInstance.getNumber(utils.numberFormat.E164) ||
          itiInstance.getNumber(utils.numberFormat.INTERNATIONAL) ||
          inputEl.value;
      } else {
        storedValue = itiInstance.getNumber() || inputEl.value;
      }

      setFormData((prev) => ({ ...prev, telefone: storedValue ?? "" }));

      const hasValue = inputEl.value.trim().length > 0;
      const shouldValidate = (phoneTouched || markTouched) && isBrazil;

      if (!hasValue) {
        if (phoneTouched || markTouched) {
          setPhoneError("Informe o telefone");
        } else {
          setPhoneError("");
        }
        return;
      }

      if (!isBrazil) { setPhoneError(""); return; }
      if (!shouldValidate) { setPhoneError(""); return; }

      if (!isValidBrazilianNumber(inputEl.value)) {
        setPhoneError("Informe DDD + numero completo");
        return;
      }

      setPhoneError("");
    },
    [getDialCodeFromDom, phoneTouched],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleExternalOpen = () => {
      setCalculatorData(null);
      setIsFormOpen(true);
    };

    const handleCalculatorOpen = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setCalculatorData({
          tipoContrato: customEvent.detail.tipoContrato,
          taxaCobrada: customEvent.detail.taxaCobrada,
          percentualAcima: customEvent.detail.percentualAcima,
          valorPagoAMais: customEvent.detail.valorPagoAMais,
        });
      }
      setIsFormOpen(true);
    };

    window.addEventListener(leadFormEventName, handleExternalOpen);
    window.addEventListener(leadFormCalculatorEventName, handleCalculatorOpen);
    return () => {
      window.removeEventListener(leadFormEventName, handleExternalOpen);
      window.removeEventListener(leadFormCalculatorEventName, handleCalculatorOpen);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const buffer = 200;
      setHideFloatingCard(scrollPosition + buffer >= documentHeight);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isFormOpen) return;

    if (intlTelInputFactoryRef.current) {
      if (!isIntlTelInputReady) setIsIntlTelInputReady(true);
      return;
    }

    let cancelled = false;

    const loadIntlTelInput = async () => {
      try {
        const [intlModule, utilsModule] = await Promise.all([
          import("intl-tel-input"),
          import("intl-tel-input/build/js/utils.js?url"),
          import("intl-tel-input/build/css/intlTelInput.css"),
        ]);

        if (cancelled) return;

        const intlFactory = (intlModule.default ?? intlModule) as IntlTelInputFactory;
        intlTelInputFactoryRef.current = intlFactory;
        intlTelInputUtilsUrlRef.current =
          typeof utilsModule.default === "string" ? utilsModule.default : null;
        setIsIntlTelInputReady(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[LeadForm] Falha ao carregar intl-tel-input", error);
      }
    };

    loadIntlTelInput();
    return () => { cancelled = true; };
  }, [isFormOpen, isIntlTelInputReady]);

  useEffect(() => {
    if (
      !isFormOpen ||
      !isPhoneInputReady ||
      !phoneInputRef.current ||
      !isIntlTelInputReady ||
      !intlTelInputFactoryRef.current
    ) {
      return;
    }

    const inputElement = phoneInputRef.current;
    const intlTelInputFactory = intlTelInputFactoryRef.current;
    const utilsScriptUrl = intlTelInputUtilsUrlRef.current ?? undefined;
    itiRef.current = intlTelInputFactory(inputElement, {
      initialCountry: "br",
      nationalMode: true,
      autoPlaceholder: "polite",
      formatOnDisplay: true,
      separateDialCode: true,
      utilsScript: utilsScriptUrl,
    });

    const handleInput = () => syncPhoneFromInput(false);
    const handleCountryChange = () => syncPhoneFromInput(false);
    const handleBlur = () => syncPhoneFromInput(true);

    inputElement.addEventListener("input", handleInput);
    inputElement.addEventListener("countrychange", handleCountryChange);
    inputElement.addEventListener("blur", handleBlur);

    return () => {
      inputElement.removeEventListener("countrychange", handleCountryChange);
      inputElement.removeEventListener("input", handleInput);
      inputElement.removeEventListener("blur", handleBlur);
      itiRef.current?.destroy();
    };
  }, [isFormOpen, isPhoneInputReady, isIntlTelInputReady, syncPhoneFromInput]);

  useEffect(() => {
    if (!isFormOpen) return;
    setHoneypotValue("");
    setFormOpenTimestamp(Date.now());
  }, [isFormOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const now = Date.now();

    if (honeypotValue.trim().length > 0) {
      toast({
        title: "Nao foi possivel enviar",
        description: "Detectamos um preenchimento automatico. Recarregue a pagina e tente novamente.",
        variant: "destructive",
      });
      return;
    }

    if (formOpenTimestamp && now - formOpenTimestamp < MIN_FORM_OPEN_TIME_MS) {
      toast({
        title: "Envio muito rapido",
        description: "Abra o formulario, revise as informacoes e tente novamente em instantes.",
        variant: "destructive",
      });
      return;
    }

    if (lastSubmissionTimestampRef.current && now - lastSubmissionTimestampRef.current < RESUBMIT_COOLDOWN_MS) {
      toast({
        title: "Aguarde antes de enviar novamente",
        description: "Estamos processando o envio anterior.",
        variant: "destructive",
      });
      return;
    }

    if (submissionLockRef.current) {
      toast({
        title: "Aguarde antes de enviar novamente",
        description: "Estamos processando o envio anterior.",
        variant: "destructive",
      });
      return;
    }

    setPhoneTouched(true);
    syncPhoneFromInput(true);

    const itiInstance = itiRef.current;
    const inputValue = phoneInputRef.current?.value.trim() ?? "";
    const selectedCountry = itiInstance?.getSelectedCountryData();
    const domDialCode = getDialCodeFromDom();
    const dialCode = domDialCode || selectedCountry?.dialCode || "";
    const isBrazil = dialCode === "55" || selectedCountry?.iso2 === "br";

    if (!formData.fullName || !formData.email || !inputValue) {
      setPhoneError((prev) => (inputValue ? prev : "Informe o telefone"));
      toast({
        title: "Campos obrigatorios",
        description: "Por favor, preencha todos os campos do formulario.",
        variant: "destructive",
      });
      return;
    }

    if (isBrazil && !isValidBrazilianNumber(inputValue)) {
      setPhoneError("Telefone invalido");
      toast({
        title: "Telefone invalido",
        description: "Verifique o numero informado e tente novamente.",
        variant: "destructive",
      });
      return;
    }

    const utils = typeof window !== "undefined" ? window.intlTelInputUtils : undefined;
    const rawPhoneValue =
      (utils && itiInstance?.getNumber(utils.numberFormat.E164)) ||
      itiInstance?.getNumber() ||
      formData.telefone ||
      inputValue ||
      "";
    const digitsOnlyPhone = rawPhoneValue.replace(/\D/g, "");
    const phoneDigitsWithDialCode =
      dialCode && digitsOnlyPhone && !digitsOnlyPhone.startsWith(dialCode)
        ? `${dialCode}${digitsOnlyPhone}`
        : digitsOnlyPhone;
    const phoneWithPlus =
      phoneDigitsWithDialCode.length > 0 ? `+${phoneDigitsWithDialCode}` : "";
    const sanitizedName = sanitizeText(formData.fullName);
    const sanitizedEmail = sanitizeText(formData.email).toLowerCase();
    const source = typeof window !== "undefined" ? window.location.href : undefined;
    const referer = typeof document !== "undefined" ? document.referrer : undefined;
    const utmParams =
      typeof window !== "undefined"
        ? Object.fromEntries(new URLSearchParams(window.location.search))
        : {};
    const facebookFbc =
      typeof document !== "undefined"
        ? getCookieValue("_fbc") || getCookieValue("fbc")
        : undefined;
    const facebookFbp =
      typeof document !== "undefined"
        ? getCookieValue("_fbp") || getCookieValue("fbp")
        : undefined;
    const stapeVisitorId =
      typeof document !== "undefined"
        ? getCookieValue("stape_user_id")
        : undefined;

    const defaultMediaId = Number(import.meta.env.VITE_DEFAULT_MEDIA_ID) || 63;
    const phoneValue = phoneWithPlus || phoneDigitsWithDialCode;

    const leadRequestPayload = {
      name: sanitizedName,
      fullName: sanitizedName,
      email: sanitizedEmail,
      telefone: phoneValue,
      phoneDigitsWithDialCode,
      phoneWithPlus,
      dialCode,
      utmParams,
      source,
      referer,
      mediaId: defaultMediaId,
      fbc: facebookFbc,
      fbp: facebookFbp,
      visitorId: stapeVisitorId,
      ...(calculatorData
        ? {
            tipoContrato: calculatorData.tipoContrato,
            taxaJurosCobrada: calculatorData.taxaCobrada,
            percentualAcimaMedia: calculatorData.percentualAcima,
            valorPagoAMaisEstimado: calculatorData.valorPagoAMais,
          }
        : {}),
    };

    try {
      submissionLockRef.current = true;
      lastSubmissionTimestampRef.current = now;
      setIsSubmitting(true);
      // eslint-disable-next-line no-console
      console.info("[LeadForm] Sending payload metadata", {
        endpoint: "/api/leads",
        fields: Object.keys(leadRequestPayload),
      });
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(leadRequestPayload),
      });
      const responseText = await response.text();
      // eslint-disable-next-line no-console
      console.info("[LeadForm] Response received", {
        status: response.status,
        body: responseText,
      });
      if (!response.ok) {
        throw new Error(
          `Falha ao enviar dados (${response.status}): ${responseText.slice(0, 500)}`,
        );
      }

      toast({
        title: "Solicitacao enviada!",
        description: "Em breve entraremos em contato com voce.",
      });

      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "formEnviadoValido",
          fullName: sanitizedName,
          email: sanitizedEmail,
          phone: phoneValue,
          utm: utmParams,
          source,
        });
      }

      itiInstance?.setNumber("");
      itiInstance?.setCountry("br");
      if (phoneInputRef.current) {
        phoneInputRef.current.value = "";
      }
      setFormData({ fullName: "", email: "", telefone: "" });
      setPhoneError("");
      setPhoneTouched(false);
      setFormOpenTimestamp(Date.now());
      setHoneypotValue("");
      setCalculatorData(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[LeadForm] Erro ao enviar", error);
      lastSubmissionTimestampRef.current = null;
      toast({
        title: "Erro ao enviar",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          "fixed bottom-4 right-4 z-30 flex items-center gap-3 transition-opacity duration-300",
          hideFloatingCard ? "opacity-0 pointer-events-none" : "opacity-100",
        )}
      >
        <button
          type="button"
          onClick={() => { setCalculatorData(null); setIsFormOpen(true); }}
          className="bg-white rounded-2xl px-3 py-2 text-left shadow-xl border border-black/5 max-w-64 hover:shadow-2xl transition-shadow text-sm md:px-4 md:py-3 md:text-base"
        >
          <p className="font-semibold text-foreground leading-tight">Ola, tudo bem?</p>
          <p className="text-xs md:text-sm text-muted-foreground leading-snug">
            Clique aqui e fale com um advogado especialista.
          </p>
        </button>
        <div className="relative h-20 w-20">
          <span className="whatsapp-wave" aria-hidden="true" />
          <span className="whatsapp-wave whatsapp-wave--delay" aria-hidden="true" />
          <Button
            size="icon"
            variant="ghost"
            className="relative h-20 w-20 p-0 overflow-hidden hover:scale-110 transition-transform bg-transparent shadow-none border-none hover:bg-transparent focus-visible:ring-0"
            onClick={() => { setCalculatorData(null); setIsFormOpen(true); }}
            aria-label="Abrir formulario de avaliacao"
          >
            <img
              src="/img/especialista.webp"
              alt="Especialista"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </Button>
        </div>
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {calculatorData
                ? "Analise gratuita do seu contrato"
                : "Fale com um advogado especialista"}
            </DialogTitle>
            <DialogDescription>
              {calculatorData
                ? "Identificamos que voce pode estar pagando juros abusivos. Nossos especialistas podem analisar seu contrato gratuitamente."
                : "Preencha o formulario para uma avaliacao inicial conduzida por nossos advogados especialistas."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fullName" className="text-foreground font-medium text-sm lg:text-base">Nome Completo *</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Seu nome"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="mt-1.5 h-11 lg:h-12 text-base"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-foreground font-medium text-sm lg:text-base">E-mail *</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1.5 h-11 lg:h-12 text-base"
                required
              />
            </div>

            <div>
              <Label htmlFor="telefone" className="text-foreground font-medium text-sm lg:text-base">Telefone/WhatsApp *</Label>
              <Input
                ref={handlePhoneInputRef}
                id="telefone"
                name="telefone"
                type="tel"
                placeholder="Ex: 11 99999-8888"
                className={cn(
                  "mt-1.5 h-11 lg:h-12 text-base",
                  phoneError && "border-destructive focus-visible:ring-destructive",
                )}
                aria-invalid={Boolean(phoneError)}
                aria-describedby={phoneError ? "telefone-error" : undefined}
                required
              />
              {phoneError && (
                <p id="telefone-error" className="mt-1 text-sm text-destructive">
                  {phoneError}
                </p>
              )}
            </div>

            <div className="hidden" aria-hidden="true">
              <Label htmlFor="empresa" className="text-foreground font-medium text-sm lg:text-base">
                Empresa
              </Label>
              <Input
                id="empresa"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypotValue}
                onChange={(e) => setHoneypotValue(e.target.value)}
                className="mt-1.5 h-11 lg:h-12 text-base"
              />
            </div>

            <Button
              type="submit"
              variant="gold"
              size="lg"
              className="w-full font-semibold text-base"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Solicitar avaliacao"}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Seus dados estao protegidos e serao utilizados apenas para contato
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeadFormDialog;
